import React from "react";
import { Crosshair, Info, Lock, Map, PencilLine, Unlock } from "lucide-react";
import { FaImage, FaVideo } from "react-icons/fa";
import { resolveCatchMediaMessage } from "./catchMediaConstants";

export default function CatchFormMediaLocationSection({ catchForm }) {
  const previewType =
    catchForm.selectedMediaType ||
    (catchForm.selectedFile?.type?.startsWith("video/") ? "video" : "image");
  const previewStatus = catchForm.mediaState?.previewStatus || "idle";
  const openFileInput = (inputId) => {
    const input =
      typeof document !== "undefined" ? document.getElementById(inputId) : null;
    if (input && typeof input.click === "function") {
      input.click();
    }
  };

  return (
    <>
      <label className="catch-form-caption-label">
        {catchForm.t("catch_caption")}:
      </label>
      <textarea
        className="catch-form-caption-textarea"
        placeholder={catchForm.t("catch_caption_placeholder")}
        value={catchForm.caption}
        onChange={(e) => catchForm.setCaption(e.target.value)}
        disabled={catchForm.isSubmitting}
      />

      <div className="catch-form-media-actions">
        <button
          type="button"
          className="catch-form-media-action-btn"
          onClick={() => openFileInput("imageUpload")}
          disabled={catchForm.isSubmitting}
        >
          <FaImage /> {catchForm.t("catch_add_photo")}
        </button>
        <input
          type="file"
          id="imageUpload"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => catchForm.handleImageUpload(e, "image")}
          disabled={catchForm.isSubmitting}
        />

        <button
          type="button"
          className="catch-form-media-action-btn"
          onClick={() => openFileInput("videoUpload")}
          disabled={catchForm.isSubmitting}
        >
          <FaVideo /> {catchForm.t("catch_add_video")}
        </button>
        <input
          type="file"
          id="videoUpload"
          accept="video/*"
          style={{ display: "none" }}
          onChange={(e) => catchForm.handleImageUpload(e, "video")}
          disabled={catchForm.isSubmitting}
        />
      </div>

      <div className="catch-form-media-helper">
        <Info size={16} strokeWidth={2.25} aria-hidden="true" />
        <span>{catchForm.t("create_post_media_optional_hint")}</span>
      </div>

      {catchForm.mediaError && (
        <div className="catch-form-message catch-form-message--error">
          {resolveCatchMediaMessage(catchForm.mediaError, catchForm.t)}
        </div>
      )}

      {catchForm.previewError && (
        <div className="catch-form-message catch-form-message--info">
          {resolveCatchMediaMessage(catchForm.previewError, catchForm.t)}
        </div>
      )}

      {!catchForm.imagePreviewUrl &&
        previewType === "image" &&
        previewStatus === "placeholder" &&
        catchForm.selectedFile && (
          <div className="catch-form-preview">
            <div className="catch-form-preview-media catch-form-preview-placeholder">
              <div>
                <div className="catch-form-preview-placeholder-title">
                  {catchForm.t("catch_photo_selected", {
                    defaultValue: "Photo selected",
                  })}
                </div>
                <div>
                  {typeof catchForm.selectedFile?.name === "string"
                    ? catchForm.selectedFile.name
                    : catchForm.t("catch_add_photo")}
                </div>
              </div>
            </div>
            {(catchForm.exifDate || catchForm.exifTime) && (
              <div className="catch-form-detected-meta">
                <div>{catchForm.t("detected_from_photo")}:</div>
                {catchForm.exifDate && <div>- {catchForm.exifDate}</div>}
                {catchForm.exifTime && <div>- {catchForm.exifTime}</div>}

                <button
                  type="button"
                  className="catch-form-link-btn"
                  onClick={() =>
                    catchForm.setUseManualDateTime((value) => !value)
                  }
                >
                  {catchForm.t("edit_date_time")}
                </button>
              </div>
            )}
          </div>
        )}

      {catchForm.imagePreviewUrl && (
        <div className="catch-form-preview">
          {previewType === "image" ? (
            <img
              src={catchForm.imagePreviewUrl}
              alt={catchForm.t("catch_media_preview_alt")}
              className="catch-form-preview-media"
              onError={catchForm.handleImagePreviewError}
            />
          ) : (
            <video
              src={catchForm.imagePreviewUrl}
              controls
              className="catch-form-preview-media"
            />
          )}
          {(previewType === "video" ||
            catchForm.exifDate ||
            catchForm.exifTime) && (
            <div className="catch-form-detected-meta">
              {previewType === "video" ? (
                <>
                  <div>
                    <strong>{catchForm.t("date", { defaultValue: "Date" })}:</strong>
                    {catchForm.exifDate ? ` ${catchForm.exifDate}` : ""}
                  </div>
                  <div>
                    <strong>{catchForm.t("time", { defaultValue: "Time" })}:</strong>
                    {catchForm.exifTime ? ` ${catchForm.exifTime}` : ""}
                  </div>
                </>
              ) : (
                <>
                  <div>{catchForm.t("detected_from_photo")}:</div>
                  {catchForm.exifDate && <div>- {catchForm.exifDate}</div>}
                  {catchForm.exifTime && <div>- {catchForm.exifTime}</div>}
                </>
              )}

              <button
                type="button"
                className="catch-form-link-btn"
                onClick={() =>
                  catchForm.setUseManualDateTime((value) => !value)
                }
              >
                {catchForm.t("edit_date_time")}
              </button>
            </div>
          )}
        </div>
      )}

      {catchForm.useManualDateTime && (
        <div className="catch-form-manual-datetime">
          <input
            type="date"
            value={catchForm.exifDate}
            onChange={catchForm.handleManualDateChange}
          />

          <input
            type="time"
            value={catchForm.exifTime}
            onChange={catchForm.handleManualTimeChange}
          />
        </div>
      )}
    </>
  );
}

export function CatchFormLocationSection({ catchForm }) {
  const shouldShowLocationActions = catchForm.locationMode === "none";

  return (
    <div className="catch-form-field">
      <label>{catchForm.t("catch_coordinates_location")}:</label>

      {shouldShowLocationActions && (
        <div className="catch-form-location-actions">
          <button
            type="button"
            className="catch-form-location-action-btn"
            onClick={catchForm.handleUseGps}
          >
            <Crosshair
              className="catch-form-location-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>{catchForm.t("catch_use_gps")}</span>
          </button>

          <button
            type="button"
            className="catch-form-location-action-btn"
            onClick={catchForm.handlePickFromMap}
          >
            <Map
              className="catch-form-location-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>{catchForm.t("catch_pick_from_map")}</span>
          </button>

          <button
            type="button"
            className="catch-form-location-action-btn catch-form-location-action-btn--manual"
            onClick={catchForm.handleStartManualCoordinates}
          >
            <PencilLine
              className="catch-form-location-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>{catchForm.t("catch_add_manually")}</span>
          </button>
        </div>
      )}

      {catchForm.locationMode !== "none" && (
        <div className="coords-input-wrapper">
          {catchForm.locationMode === "manual" && (
            <button
              type="button"
              className="coords-manual-back-btn"
              onClick={catchForm.handleCancelManualCoordinates}
            >
              {catchForm.t("catch_manual_coordinates_back", {
                defaultValue: "Back",
              })}
            </button>
          )}

          {catchForm.locationHint && (
            <div className="coords-hint">{catchForm.locationHint}</div>
          )}

          <input
            type="text"
            placeholder={catchForm.t("catch_coordinates_placeholder")}
            value={catchForm.coordinates}
            onChange={catchForm.handleCoordinatesChange}
            onBlur={catchForm.handleCoordinatesBlur}
            className="coords-input"
          />

          {catchForm.coordinates && (
            <div className="coords-locks">
              <button
                type="button"
                className={`coords-lock-btn ${catchForm.isLocationPublic ? "active" : ""}`}
                onClick={catchForm.handleSetLocationPublic}
                title={catchForm.t("catch_location_public")}
              >
                <Unlock size={16} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`coords-lock-btn ${!catchForm.isLocationPublic ? "active" : ""}`}
                onClick={catchForm.handleSetLocationPrivate}
                title={catchForm.t("catch_location_private")}
              >
                <Lock size={16} strokeWidth={2.4} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="coords-lock-btn coords-clear-btn"
                onClick={catchForm.handleClearCoordinates}
                title={catchForm.t("catch_clear_coordinates")}
              >
                {catchForm.t("catch_clear")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
